import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentRapForecastQueryOptions,
  rapForecastDetailsExportQueryOptions,
  rapForecastDetailsRawExportQueryOptions,
  rapForecastDetailsSearchSchema,
  useRapForecastById,
  useUpdateRapForecast,
} from '@/hooks/queries/crt/useRapForecasts'
import { useLastUpdatedRapForecastsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { UpdateRapForecast } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import RapForecastsDetailsDataTable from './-components/RapForecastsDetails/RapForecastsDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/rap-forecasts/$year/$month',
)({
  component: RapForecastsDetailsPeriod,
  validateSearch: (search) => rapForecastDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const rapForecast = crtPeriod
      ? await queryClient.fetchQuery(currentRapForecastQueryOptions(crtPeriod))
      : null

    return {
      crumb: rapForecast?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      rapForecastId: rapForecast ? String(rapForecast.id) : null,
    }
  },
})

function RapForecastsDetailsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, rapForecastId } = Route.useLoaderData()

  if (!rapForecastId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/customer-investments/insights/rap-forecasts"
      />
    )
  }

  return <RapForecastsDetails key={rapForecastId} rapForecastId={rapForecastId} />
}

function RapForecastsDetails({ rapForecastId }: { rapForecastId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useRapForecastById(rapForecastId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedRapForecastTrail } = useLastUpdatedRapForecastsLog(rapForecastId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateRapForecast | null => {
    if (!data) return null

    const changes: UpdateRapForecast = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedRapForecastTrail)

  const { mutate: updateRapForecast, isPending: isUpdatePending } = useUpdateRapForecast({
    rapForecastId: Number(rapForecastId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateRapForecast(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.rapForecasts.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28" maxWidth="wide">
      <HStack gap="sm" className="items-start justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_rap_forecasts_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>
          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      rapForecastDetailsExportQueryOptions(rapForecastId, search),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      rapForecastDetailsRawExportQueryOptions(rapForecastId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/customer-investments/insights/rap-forecasts"
              uploadType="import-rap-forecasts-details"
              uploadAttributes={{ 'rap-forecast-id': rapForecastId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <RapForecastsDetailsDataTable
        rapForecastId={rapForecastId}
        rapForecast={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_rap_forecasts_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
